import styles from "./ListCardItem.module.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DateTimeService from "../../Services/datetimeService";
import { useDeletionConfirmationModal } from "../../Hooks/DeletionConfirmationModal";
import i18n from "../../Services/i18n";

import clientIcon from "../../Assets/Icons/myaccount.png";
import horseIcon from "../../Assets/Icons/horse_black.png";
import serviceIcon from "../../Assets/Icons/service_black.png";

import {
  MdDelete,
  MdVisibility,
  MdDateRange,
  MdAccessTime,
  MdCalendarMonth,
  MdHistory,
  MdNotificationImportant,
} from "react-icons/md";

import defaultClientThumbnail from "../../Assets/Default/default_client.png";
import defaultHorseThumbnail from "../../Assets/Default/default_horse.png";
import defaultServiceThumbnail from "../../Assets/Default/default_serviceoccurence.png";
import UpsertServiceOccurenceModal from "../Modal/UpsertServiceOccurenceModal";
import ImageViewer from "../ImageViewer/ImageViewer";
import { addToast } from "../../Services/toastService";
import ClientService from "../../Services/clientService";
import HorseService from "../../Services/horseService";
import ServiceOccurenceService from "../../Services/serviceOccurenceService";
import UserServiceService from "../../Services/userServiceService";
import { useModal } from "../../Context/ModalContext";

const ListCardItem = React.forwardRef(
  (
    { id, dataItem, date, isSelected, onClick, type, refresh, displayMode, showContextMenu = true },
    ref,
  ) => {
    const [show, setShow] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const confirmDelete = useDeletionConfirmationModal();

    const { openModal } = useModal();
    const serviceModalRef = useRef(null);
    let navigate = useNavigate();

    const altTxt = i18n.t("ListCardItem.ImgAltDefault");
    const [showImageModal, setShowImageModal] = useState(false);

    const handleImageClick = () => {
      if (dataItem.defaultImageId) {
        setShowImageModal(true);
      }
    };

    useEffect(() => {
      setShow(true);
    }, []);

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const handleCloseImageViewer = useCallback(() => setShowImageModal(false), []);

    const getDeletionFunction = () => {
      switch (type) {
        case "client":
          return (entityId) => ClientService.DeleteClient(entityId);
        case "horse":
          return (entityId) => HorseService.DeleteHorse(entityId);
        case "serviceoccurence":
          return (entityId) => ServiceOccurenceService.DeleteServiceOccurence(entityId);
        case "userservice":
          return (entityId) => UserServiceService.DeleteUserServices(entityId);
        default:
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return () => {
            console.log("error getDeletionFunction");
          };
      }
    };

    const getTargetEntityIconSrc = () => {
      switch (dataItem.targetEntityType) {
        case "horse":
          return horseIcon;
        case "client":
          return clientIcon;
        default:
          return null;
      }
    };

    function htmlToText(html) {
      const div = document.createElement("div");
      div.innerHTML = html ?? "";
      return div.textContent || div.innerText || "";
    }

    const getDefaultImage = () => {
      if (dataItem.defaultImage) {
        return <img src={dataItem.defaultImage} alt={altTxt} />;
      }

      switch (type) {
        case "client":
          return <img src={defaultClientThumbnail} alt={altTxt} />;
        case "horse":
          return <img src={defaultHorseThumbnail} alt={altTxt} />;
        case "serviceoccurence":
          return <img src={defaultServiceThumbnail} alt={altTxt} />;
        default:
          return <img src={defaultClientThumbnail} alt={altTxt} />;
      }
    };

    const renderTargetEntityMarker = () => {
      if (type !== "serviceoccurence" || !dataItem.targetEntityName) return null;

      const tooltip = dataItem.targetEntityName;
      const iconSrc = getTargetEntityIconSrc();

      return (
        <div
          className={`${styles.targetEntityInline} ${
            displayMode === "compact" ? styles.targetEntityInlineCompact : ""
          }`}
          title={tooltip}>
          {iconSrc && (
            <img
              src={iconSrc}
              alt={tooltip}
              title={tooltip}
              className={styles.targetEntityInlineImage}
            />
          )}

          {displayMode !== "compact" && (
            <span className={styles.targetEntityInlineText} title={tooltip}>
              {dataItem.targetEntityName}
            </span>
          )}
        </div>
      );
    };
    const getDaysSinceLastService = (date) => {
      if (!date) return null;

      const serviceDate = new Date(date);
      const today = new Date();

      serviceDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return Math.floor((today - serviceDate) / (1000 * 60 * 60 * 24));
    };

    const getDaysAgo = (date) => {
      if (!date) return null;

      const serviceDate = new Date(date);
      const today = new Date();

      serviceDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      const diffTime = today - serviceDate;
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    };
    const daysAgo = getDaysAgo(date);

    const getDate = () => {
      switch (type) {
        case "client":
          return (
            <div
              className={styles.cardDate}
              style={{ fontStyle: "italic" }}
              title={DateTimeService.formatDateString(date, true, true)}>
              {date && (
                <span>
                  {i18n.t("Generic.CreationDate")} {DateTimeService.formatDateString(date)}
                </span>
              )}
            </div>
          );
        case "horse":
          return (
            <div
              className={styles.cardDate}
              style={{ fontStyle: "italic" }}
              title={DateTimeService.formatDateString(date, true, true)}>
              {date && (
                <span>
                  {i18n.t("Generic.CreationDate")} {DateTimeService.formatDateString(date)}
                </span>
              )}
            </div>
          );
        case "serviceoccurence":
          return (
            <div
              className={styles.cardDateImportant}
              title={
                DateTimeService.formatDateString(date, true, true) +
                " " +
                i18n.t("ServiceOccurenceForm.DaysAgo", { count: daysAgo })
              }>
              <span className={styles.line}>
                <MdDateRange className={styles.icon} />
                <span className={styles.text}>{DateTimeService.formatDateString(date, false)}</span>
              </span>
              <span className={styles.line}>
                <MdAccessTime className={styles.icon} />
                <span className={styles.text}>{DateTimeService.extractTime(date)}</span>
              </span>
            </div>
          );
        default:
          return (
            <div
              className={styles.cardDate}
              title={DateTimeService.formatDateString(date, true, true)}>
              {DateTimeService.formatDateString(date)}
            </div>
          );
      }
    };

    const handleViewDetailClick = (showSpecificService) => {
      const hasSpecificService =
        showSpecificService !== undefined &&
        showSpecificService !== null &&
        showSpecificService !== "";

      const targetType = hasSpecificService ? "serviceoccurence" : type;
      const targetId = hasSpecificService ? showSpecificService : id;

      switch (targetType) {
        case "client":
          navigate("/clients", { state: { paramClientId: targetId } });
          break;

        case "horse":
          navigate("/horses", {
            state: {
              paramHorseId: targetId,
              paramHorseClientId: dataItem.clientExternalId,
            },
          });
          break;

        case "serviceoccurence":
          setTimeout(async () => {
            openModal({
              title:
                i18n.t("BoardServiceClient.ServiceOccurenceLastService") +
                " " +
                i18n.t("BoardServiceClient.ServiceOccurenceUpdate"),
              form: "formServiceOccurence",
              wideMode: true,
              hideSaveButton: false,
              hideSaveAndCloseButton: false,
              hideGenerateReportButton: false,
              generateReportLabel: i18n.t("Modal.GenerateReport.ServiceReport"),

              canClose: async () => {
                const isDirty = serviceModalRef.current?.isDirty?.();
                if (!isDirty) return true;

                return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
              },

              handleGenerateReport: async () => {
                const id = await serviceModalRef.current.serviceOccurenceExternalId();

                await ServiceOccurenceService.GenerateServiceReport(id);
              },

              onSave: async () => {
                const isValid = await serviceModalRef.current.validate();
                if (!isValid) return;

                return await serviceModalRef.current.save();
              },

              onClose: () => {},

              content: (
                <UpsertServiceOccurenceModal
                  key={targetId}
                  ref={serviceModalRef}
                  serviceOccurenceExternalId={targetId}
                  onSave={async (svcOccExternalId) => {
                    await refresh(svcOccExternalId);
                  }}
                />
              ),
            });
          }, 0);

          break;

        default:
          break;
      }
    };

    /*const handleViewNoteClick = () => {};*/

    const handleDeleteClick = () => {
      let icon = null;
      switch (type) {
        case "client":
          icon = clientIcon;
          break;
        case "horse":
          icon = horseIcon;
          break;
        case "serviceoccurence":
          icon = serviceIcon;
          break;
        case "userservice":
          icon = serviceIcon;
          break;

        default:
          icon = null;
      }

      confirmDelete({
        entityId: dataItem.externalId,
        entityTitle: dataItem.cardName,
        entitySubTitle:
          type === "serviceoccurence" ? DateTimeService.formatDateString(date, true) : null,
        icon: icon,
        entityImage: dataItem.defaultImage,
        deleteFunction: getDeletionFunction(),
        onClose: async (refreshNeeded) => {
          if (refreshNeeded && typeof refresh === "function") {
            await refresh();
          }
        },
      });
    };

    const handleViewCalendarClick = () => {
      switch (type) {
        case "client":
          navigate("/calendar", { state: { paramClientExternalId: id } });
          break;
        case "horse":
          navigate("/calendar", { state: { paramHorseExternalId: id } });
          break;
        case "userservice":
          navigate("/calendar", { state: { paramUserServiceExternalId: id } });
          break;
        default:
          break;
      }
    };

    const ActionMenu = ({ isVisible }) => (
      <div className={`${styles.actionMenu} ${isVisible ? styles.visible : ""}`}>
        <MdVisibility
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetailClick();
          }}
        />
        {["client", "horse", "userservice"].includes(type) && (
          <MdCalendarMonth
            onClick={(e) => {
              e.stopPropagation();
              handleViewCalendarClick();
            }}
          />
        )}
        {["client", "horse"].includes(type) && dataItem.lastServiceDate && (
          <MdHistory
            title={`${i18n.t("cards.lastServiceAgo", {
              count: getDaysSinceLastService(dataItem.lastServiceDate),
            })}\n\n${dataItem.lastServiceMsg}`}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetailClick(dataItem.lastServiceExternalId);
            }}
          />
        )}
        <MdDelete
          className={styles.actionMenuDelete}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }}
        />
      </div>
    );
    return (
      <div
        ref={ref}
        onClick={() => {
          onClick(dataItem);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${styles.item} ${styles[displayMode]} ${show ? styles.show : ""} ${
          isSelected ? styles.itemSelected : ""
        } ${isHovered ? styles.itemHovered : ""} ${
          dataItem.isArchived ? styles.itemArchived : ""
        }`}>
        {showImageModal && (
          <ImageViewer
            id={dataItem.defaultImageId}
            onClose={() => {
              handleCloseImageViewer();
            }}
          />
        )}

        {showContextMenu && <ActionMenu isVisible={isHovered || isSelected} />}
        <div className={styles.contentContainer}>
          {type === "horse" && dataItem.hasActiveNotification && (
            <div
              className={styles.notificationBadge}
              title={i18n.t("Notification.ActiveNotification")}
              onClick={(e) => {
                e.stopPropagation();

                navigate("/horses", {
                  state: {
                    paramHorseId: dataItem.externalId,
                    paramHorseTab: "Notifications",
                  },
                });
              }}>
              <MdNotificationImportant />
            </div>
          )}
          <div className={styles.imgContainer} onClick={handleImageClick}>
            {getDefaultImage()}
          </div>
          <div className={styles.textContainer}>
            <div className={styles.headerRow}>
              <div className={styles.cardTextZone}>
                <div className={styles.cardNameRow}>
                  <div className={styles.cardName} title={dataItem.cardName}>
                    {dataItem.cardName ?? dataItem.title}
                  </div>

                  {dataItem.isArchived && (
                    <span className={styles.archivedBadge}>{i18n.t("Generic.Archived")}</span>
                  )}
                </div>

                {renderTargetEntityMarker()}

                {!(type === "serviceoccurence" && dataItem.targetEntityName) &&
                  dataItem.targetEntityName && <div>{dataItem.targetEntityName}</div>}

                <div
                  className={styles.cardDescription}
                  title={htmlToText(dataItem.description)}
                  dangerouslySetInnerHTML={{
                    __html: dataItem.description,
                  }}></div>
              </div>

              <div className={styles.cardDateZone}>{getDate()}</div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default ListCardItem;
